/**
 * CALL-CENTER-AGENT-SCOPE-AND-DISPOSITION-CORE-V1 -- the "Last contact
 * attempt" summary card. Derives PURELY from the SAME Lead 360 wire the
 * timeline consumes (no second read, no second truth); labeled fields only
 * (the render law), PHI-free. Pure/SSR-testable (react-dom/server).
 *
 * Honest empty state when the lead has no recorded disposition yet.
 * Evidence is always labeled "Agent-recorded" (this package mints no
 * provider-verified evidence).
 */

import type { Lead360Input } from '@/dashboard/owner/lead-360-view';
import { formatTimestampIst } from '@/dashboard/owner/Lead360Panel';
import {
  humanizeDispositionSlug,
  resolveLastContactAttempt,
} from '@/dashboard/leads/last-contact-attempt';

export const LAST_CONTACT_EMPTY_COPY = 'No contact attempt recorded yet.';
export const LAST_CONTACT_AGENT_BADGE = 'Agent-recorded';

function Field({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId: string;
}): JSX.Element {
  return (
    <div className="lead-last-contact__field" data-testid={testId}>
      <span className="lead-last-contact__label">{label}</span>
      <span className="lead-last-contact__value">{value}</span>
    </div>
  );
}

export default function LastContactAttemptSummary({
  record,
}: {
  record: Lead360Input;
}): JSX.Element {
  const last = resolveLastContactAttempt(record);
  return (
    <section
      className="lead-last-contact lead360-section"
      aria-label="Last contact attempt"
      data-testid="lead-last-contact"
    >
      <h4 className="lead360-section__title">Last contact attempt</h4>
      {last === null ? (
        <p className="lead360-muted" data-testid="lead-last-contact-empty">
          {LAST_CONTACT_EMPTY_COPY}
        </p>
      ) : (
        <div className="lead-last-contact__grid">
          <Field
            label="When"
            value={formatTimestampIst(last.atIso)}
            testId="lead-last-contact-when"
          />
          <Field
            label="Channel"
            value={humanizeDispositionSlug(last.channel) ?? 'Not recorded'}
            testId="lead-last-contact-channel"
          />
          <Field
            label="Result"
            value={humanizeDispositionSlug(last.resultId) ?? 'Not recorded'}
            testId="lead-last-contact-result"
          />
          {last.connectId !== null ? (
            <Field
              label="They said"
              value={humanizeDispositionSlug(last.connectId) ?? ''}
              testId="lead-last-contact-connect"
            />
          ) : null}
          {last.nextActionId !== null ? (
            <Field
              label="Next action"
              value={humanizeDispositionSlug(last.nextActionId) ?? ''}
              testId="lead-last-contact-next"
            />
          ) : null}
          {last.callbackAtIso !== null ? (
            <Field
              label="Callback due"
              value={formatTimestampIst(last.callbackAtIso)}
              testId="lead-last-contact-callback"
            />
          ) : null}
          <div className="lead-last-contact__field" data-testid="lead-last-contact-evidence">
            <span className="lead-last-contact__label">Evidence</span>
            <span className="ac-tag" data-testid="lead-last-contact-agent-badge">
              {LAST_CONTACT_AGENT_BADGE}
            </span>
          </div>
          <p className="lead360-muted" data-testid="lead-last-contact-summary">
            {last.summary}
          </p>
        </div>
      )}
    </section>
  );
}
